import { Formik, FormikHelpers, FormikProps } from "formik";
import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ObjectSchema } from "yup";
import { AvisConstants } from "../../data/avisConstants";
import {
  PasswordPolicySettingsViewModel,
  RequestChangePasswordViewModel,
} from "../../generated/data-contracts";
import { useSessionStorage } from "../../hooks/useSessionStorage";
import { modalLocalization } from "../../localization/modalLocalization";
import { IExtendedChangePasswordViewModel } from "../../models";
import { ErrorUtils, IAxiosError, api } from "../../utils";
import { AvisDescriptiveText } from "../shared/components/form";
import { AvisForm } from "../shared/components/index";
import { AvisConfirmModalShort } from "../shared/components/modal";
import { AvisContext } from "../shared/context/AvisContext";
import { ChangePasswordForm } from "./ChangePasswordForm";
import { changePasswordFormValidation } from "./ChangePasswordFormValidation";

export function ChangePasswordPage() {
  const { ctxUser, res, portalKind } = useContext(AvisContext);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [forgotPassword, setForgotPassword] = useState<boolean>(false);
  const [canUseEId, setCanUseEId] = useState<boolean>(true);
  const [error, setError] = useState<IAxiosError>();
  const [policiesLoading, setPoliciesLoading] = useState<boolean>(true);
  const [policies, setPolicies] =
    useState<PasswordPolicySettingsViewModel | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [, setReturnPortalType] = useSessionStorage(
    AvisConstants.eId_return_to_portal_session_key,
  );

  const validationSchema:
    | ObjectSchema<IExtendedChangePasswordViewModel>
    | undefined = changePasswordFormValidation(
    policies,
    forgotPassword,
    ctxUser,
    res,
  );

  useEffect(() => {
    if (policies == null) {
      api
        .securityPasswordSettings()
        .then((res) => setPolicies(res.data))
        .catch(setError)
        .finally(() => setPoliciesLoading(false));

      return;
    }

    setCanUseEId(ctxUser?.personContactId === 0);
    const query = new URLSearchParams(location.search);

    if (query.get("success") === "1") {
      searchParams.delete("success");
      setSearchParams(searchParams);
      onSuccess();
    }
    if (query.get("error")) {
      onFail();
    }

    const serverErrorMessage = query.get("server_error");
    const serverErrorCode = query.get("server_error_code");
    if (serverErrorMessage && serverErrorCode) {
      onServerFail(serverErrorMessage, serverErrorCode);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctxUser, policies]);

  const onSuccess = () => {
    ErrorUtils.showSuccessfulMessage(
      res("message_Success"),
      res("user_ChangePassword_PasswordChangedSuccessfully"),
      AvisConstants.notification_async_default_timeout,
    );
  };
  const onServerFail = (errorMessage: string, errorCode: string) => {
    //server_error
    searchParams.delete("server_error");
    searchParams.delete("server_error_code");
    setSearchParams(searchParams);
    ErrorUtils.showErrorMessage(
      {
        errorMessage: errorMessage,
        errorCode: errorCode,
      },
      res,
      AvisConstants.notification_async_default_timeout,
    );
  };
  const onFail = () => {
    let logCodeString = "";
    api
      .logPostErrorData({
        errorData: `${res("user_ChangePassword_PasswordNotChanged")} response:${
          location.search
        }`,
      })
      .then((response) => {
        logCodeString = response.data;
      })
      .finally(() => {
        //Clear params from query and show error message
        searchParams.delete("error");
        searchParams.delete("state");
        setSearchParams(searchParams);
        ErrorUtils.showErrorMessage(
          {
            errorMessage: res("user_ChangePassword_PasswordNotChanged"),
            errorCode: logCodeString,
          },
          res,
          AvisConstants.notification_async_default_timeout,
        );
      });
  };

  const onSubmit = (
    values: IExtendedChangePasswordViewModel,
    formikHelpers: FormikHelpers<IExtendedChangePasswordViewModel>,
  ) => {
    setSubmitting(true);
    if (!values.loginType) return;
    if (values.loginType === "regular") {
      api
        .securityChangePassword(values)
        .then(() => {
          formikHelpers.resetForm();
          onSuccess();
        })
        .catch((error) => ErrorUtils.handleFormValidation(error, formikHelpers))
        .finally(() => {
          setSubmitting(false);
          setIsModalOpen(false);
        });

      return;
    }

    // "sc_plugin" || "mobile"
    api
      .securityRequestChangePassword(values as RequestChangePasswordViewModel)
      .then((res) => {
        if (!res.data?.redirectUrl) {
          throw Error("redirectUrl is not retrieved");
        }
        setReturnPortalType(portalKind.portalType);
        window.location.assign(res.data.redirectUrl);
        setSubmitting(false);
      })
      .catch((error) => {
        ErrorUtils.handleFormValidation(error, formikHelpers);
        setSubmitting(false);
      });
  };

  if (!ctxUser) {
    return;
  }
  const renderForm = (
    formikProps: FormikProps<IExtendedChangePasswordViewModel>,
  ) => (
    <>
      <ChangePasswordForm
        bag={formikProps}
        loading={submitting}
        canUseEId={canUseEId}
        forgotPassword={forgotPassword}
        setForgotPassword={setForgotPassword}
        policies={policies}
        showModal={() => setIsModalOpen(true)}
      />
      <AvisConfirmModalShort
        isOpen={isModalOpen}
        content={res("user_ChangePassword_ModalConfirm")}
        confirm={formikProps.submitForm}
        cancel={() => setIsModalOpen(false)}
        localization={modalLocalization(res)}
        title={res("title_Confirm")}
        hideClose
        disableControls={submitting}
      />
    </>
  );

  return (
    <AvisForm
      className="change-password-form"
      error={error}
      loading={policiesLoading}
      title={res("user_ChangePassword_ChangePasswordTitle")}
    >
      <AvisDescriptiveText textKey="portal_Profile_ChangePassword_1" />
      {ctxUser?.hasPassword ? (
        <Formik<IExtendedChangePasswordViewModel>
          initialValues={{
            oldPassword: "",
            password1: "",
            password2: "",
            personId: ctxUser.personId,
            personContactId: ctxUser.personContactId,
            loginType: "regular",
            redirectUrl: window.location.href,
          }}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
          validateOnBlur={false}
          validateOnChange={true}
        >
          {renderForm}
        </Formik>
      ) : (
        <p>{res("user_ChangePassword_CannotChangePassword")}</p>
      )}
    </AvisForm>
  );
}
